import { IAccessRole, PermissionGrants } from 'app/models/permission.model';
import { UserRoleLevel } from 'app/models/user.model';
import { PermissionService } from 'app/services/permission.service';

/**
 * Role selector rules shared by the staff edit form and the Access details panel, so both offer the
 * same roles and the same limits. They mirror the API (AccessRoleService): nobody gives more than
 * they have.
 */

/** A built-in role in the selector */
export interface BuiltInRoleOption {
  level: number;
  nameKey: string;
  /** Shown only when the customer has one of these features */
  features?: string[];
  /** Offered only to Everon admins (customers see it only as a staff member's current role) */
  everonAdminOnly?: boolean;
}

/**
 * Built-in roles of the role selector, lowest first. The Everon Mobile App, OnOn and EMAAC Operator roles
 * need the feature. EMAAC Operators and EMAAC accounts are managed by Everon admins only (ES-7530).
 */
export const BUILT_IN_ROLE_OPTIONS: readonly BuiltInRoleOption[] = [
  { level: 0, nameKey: 'STAFF_READONLY' },
  { level: 10, nameKey: 'EveronMobileApp', features: ['EveronMobileApp'] },
  { level: 40, nameKey: 'STAFF_ONON_MAIN_USER', features: ['AnnaPerenna', 'OnOn'] },
  { level: 50, nameKey: 'STAFF_USER' },
  { level: 100, nameKey: 'STAFF_MAIN_USER' },
  { level: 101, nameKey: 'STAFF_CUSTOMER_ADMIN' },
  { level: UserRoleLevel.EmaacOperator, nameKey: 'STAFF_EMAAC_OPERATOR', features: ['EMAAC'], everonAdminOnly: true },
  { level: UserRoleLevel.ControlRoomUser, nameKey: 'STAFF_EMAAC_ACCOUNT', everonAdminOnly: true },
];

/** EMAAC Operators and EMAAC accounts have a fixed role that only Everon admins set, through the level */
export function isEmaacRole(level: number): boolean {
  return level === UserRoleLevel.EmaacOperator || level === UserRoleLevel.ControlRoomUser;
}

/** What the user giving roles has: their level, their permissions and the built-in roles' grants */
export interface RoleGrantContext {
  userLevel: number;
  permissions: Pick<PermissionService, 'covers'>;
  /** Built-in role grants by level (from GET api/AccessRoles), needed for users on a custom role */
  builtInGrants: Record<number, PermissionGrants>;
}

/** The staff member's current role: a custom role, or the built-in role of the level */
export interface CurrentRole {
  accessRoleId: number | null;
  level: number;
}

/** Role selector value: "custom:<id>" or "builtIn:<level>" */
export function roleChoice(accessRoleId: number | null, level: number): string {
  return accessRoleId ? `custom:${accessRoleId}` : `builtIn:${level ?? 0}`;
}

/** The assignment a selector value stands for, as PUT api/AccessRoles/Staff/{id}/Role takes it */
export function parseRoleChoice(choice: string): { accessRoleId: number } | { builtInLevel: number } | null {
  const [kind, value] = (choice ?? '').split(':');
  if (kind === 'custom' && value) return { accessRoleId: Number(value) };
  if (kind === 'builtIn' && value) return { builtInLevel: Number(value) };
  return null;
}

/**
 * Built-in roles offered for this customer, plus the staff member's current one even without the
 * feature. Everon-admin-only roles are offered to Everon admins (userLevel SystemAdmin) only.
 */
export function visibleBuiltInRoles(featureEnabled: (feature: string) => boolean, current: CurrentRole,
                                    userLevel?: number): BuiltInRoleOption[] {
  const isCurrent = (option: BuiltInRoleOption) => !current.accessRoleId && option.level === current.level;
  return BUILT_IN_ROLE_OPTIONS.filter(option =>
    isCurrent(option) ||
    ((!option.everonAdminOnly || userLevel === UserRoleLevel.SystemAdmin) &&
      (!option.features || option.features.some(featureEnabled))));
}

/** Built-in role grants by level, from the role list */
export function builtInGrantsOf(roles: IAccessRole[]): Record<number, PermissionGrants> {
  return Object.fromEntries((roles ?? []).filter(r => r.builtIn).map(r => [r.level, r.grants]));
}

/**
 * May the user give this built-in role. Users on a built-in role keep the level ceiling (a customer
 * admin can make another customer admin), users on a custom role (level 0) may give only what they
 * have themselves.
 */
export function canGrantBuiltIn(context: RoleGrantContext, level: number): boolean {
  if (context.userLevel === UserRoleLevel.SystemAdmin) return true;
  // Only Everon admins set (or take away) the EMAAC roles; their numbers are no ceiling to compare with
  if (isEmaacRole(level)) return false;
  if (context.userLevel === UserRoleLevel.ReadOnly) {
    // The Everon Mobile App level has the read-only user's grants
    const grants = context.builtInGrants[level === UserRoleLevel.EMAOnly ? UserRoleLevel.ReadOnly : level];
    return !!grants && context.permissions.covers(grants);
  }
  return level <= context.userLevel;
}

/** May the user give this custom role: only with all of its access */
export function canGrantCustom(context: RoleGrantContext, role: IAccessRole): boolean {
  return context.userLevel === UserRoleLevel.SystemAdmin || context.permissions.covers(role.grants);
}

/** The staff member has more access than the user, so the role can't be changed (the API refuses it) */
export function isRoleAboveOwnAccess(context: RoleGrantContext, current: CurrentRole, customRoles: IAccessRole[]): boolean {
  if (current.accessRoleId) {
    const role = customRoles.find(r => r.id === current.accessRoleId);
    return !!role && !canGrantCustom(context, role);
  }
  return !canGrantBuiltIn(context, current.level);
}
